import { ArticleResolution, ArticleType } from '@wix/answers-api';
import { pageClick } from '@wix/bi-logger-customer-care-data/v2';
import { ConsumerSource, Anchors } from '@wix/cc-user-facing-common-components';
import { Box, Heading, Text } from '@wix/design-system';
import { useExperiments } from '@wix/fe-essentials-standalone';
import { useTranslation } from '@wix/wix-i18n-config';
import { TimerSmall } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { FunctionComponent } from 'react';

import { ArticlePublishedDate } from './ArticlePublishedDate';
import { ArticleStatusBanner } from './ArticleStatusBanner';
import { BI, EXPERIMENTS, LOCALES } from '../../../constants';
import { DATA_HOOKS } from '../../../dataHooks';
import { useBI } from '../../../hooks/useBI';
import useIsHelpCenterCopilotEnabled from '../../../hooks/useIsHelpCenterCopilotEnabled';
import { Step } from '../../../pages/article/[articleTitle]';
import { mapArticleTypeToItemType } from '../../../utils/bi';
import { useStore } from '../../../zustand/stores';
import { RoadmapResolutionEta } from '../../RoadmapResolutionEta';
import KeraAssistant from '../KeraAssistant';

import css from './index.module.scss';

const ANCHORS_SUPPORTED_LOCALES = [
  LOCALES.EN,
  LOCALES.PT,
  LOCALES.ES,
  LOCALES.KO,
  LOCALES.TR,
  LOCALES.NL,
  LOCALES.IT,
  LOCALES.FR,
  LOCALES.DE,
  LOCALES.JA,
];

export const KERA_ASSISTANT_SUPPORTED_ARTICLE_IDS = [
  'a85fe243-6375-49a1-b825-1b8c8717005e', // Connecting a Domain to the Wix Name Servers
  'e72873df-9108-4665-a12b-6b077e9d453f', // Connecting a Domain Purchased from Wix
  '84590ba1-1da3-4b8a-919d-59f8ecce045e', // Assigning a Domain to a Site in Your Wix Account
  '8d62a3cd-3a66-4a94-a97c-22c4d7e44524', // Transferring a Premium Site to Another Wix Account
  '88f9af56-d2c4-47e7-8d9c-3d831da6f5f5', // Connecting or Transferring a Domain Purchased Elsewhere to Your Wix Account
  '5358847b-2d40-4fb7-bb04-b562b91758e7', // Assigning a Premium Plan to a Different Site in Your Wix Account
  '9c73cfd9-723c-4bbc-9692-f9f3faea0885', // Identifying and Reporting Phishing
];

export type ArticleHeaderProps = {
  type: ArticleType;
  title: string;
  url: string;
  creationDate: number | Long;
  resolution?: ArticleResolution;
  resolutionEta?: Long;
  isRoadmapArticle?: boolean;
  readingTime?: number;
  onClickStatus?: (resolution: ArticleResolution) => void;
  steps: Step[];
};

export const ArticleHeader: FunctionComponent<ArticleHeaderProps> = ({
  type,
  resolution,
  resolutionEta,
  title,
  url,
  isRoadmapArticle,
  readingTime,
  creationDate,
  onClickStatus,
  steps,
}: ArticleHeaderProps) => {
  const { locale = '' } = useRouter();
  const { t } = useTranslation();
  const { sendBIEvent } = useBI();
  const itemId = useStore((state) => state.pageSlice.itemId);
  const { user } = useStore((state) => state.userSlice);
  const { experiments } = useExperiments({ readOnly: true });

  const isHelpCenterCopilotEnabled = useIsHelpCenterCopilotEnabled();
  const isKeraAssistantEnabled = experiments.enabled(
    EXPERIMENTS.SPECS.KERA_VOICE_ASSISTANT
  );

  const shouldShowKeraAssistant =
    isKeraAssistantEnabled &&
    locale === LOCALES.EN &&
    KERA_ASSISTANT_SUPPORTED_ARTICLE_IDS.includes(itemId as string) &&
    user !== null;

  const onAnchorClick = async (text: string, order: string) => {
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.ARTICLE,
        kb_lang: locale,
        item_id: itemId,
        item_type: mapArticleTypeToItemType(type),
        clicked_item_type: BI.CLICKED_ITEM_TYPES.INTERNAL_ANCHOR,
        clicked_text: text,
        clicked_item_order: order,
        is_page_content_click: true,
      })
    );
  };

  const showReadingTime =
    type === ArticleType.ARTICLE && readingTime && readingTime > 0;

  return (
    <>
      <Heading
        size="extraLarge"
        className={classNames(css.articleTitle, {
          [css.copilotArticleTitle]: isHelpCenterCopilotEnabled,
        })}
      >
        {title}
      </Heading>

      {type === ArticleType.KNOWN_ISSUE ? (
        <ArticlePublishedDate
          locale={locale as string}
          creationDate={creationDate}
        />
      ) : null}

      {isRoadmapArticle && resolution ? (
        <div className={css.articleRoadmapTags}>
          <RoadmapResolutionEta
            wrapEtaWithTag={true}
            resolution={resolution}
            resolutionEta={resolutionEta}
            onClickStatus={onClickStatus}
          />
        </div>
      ) : (
        [ArticleType.FEATURE_REQUEST, ArticleType.KNOWN_ISSUE].includes(
          type
        ) && (
          <ArticleStatusBanner
            articleResolution={resolution}
            articleType={type}
          />
        )
      )}
      <Box gap="SP4" direction="vertical" paddingTop="SP3">
        {showReadingTime ? (
          <Box className={css.timeToReadWrapper}>
            <TimerSmall color={'#868AA5'} />
            <Text
              dataHook={DATA_HOOKS.ARTICLE_READING_TIME}
              size="small"
              color="D40"
              className={css.timeToRead}
            >
              {t('informative-article-time-to-read', { time: readingTime })}
            </Text>
          </Box>
        ) : null}
        {ANCHORS_SUPPORTED_LOCALES.includes(locale) && steps.length > 0 && (
          <Box className={css.anchorsWrapper}>
            <Anchors
              steps={steps}
              onClick={onAnchorClick}
              consumerSource={ConsumerSource.HELP_CENTER}
              locale={locale as string}
            />
          </Box>
        )}
        {shouldShowKeraAssistant && (
          <KeraAssistant articleId={itemId as string} articleUrl={url} />
        )}
      </Box>
    </>
  );
};
